import { useEffect, useState, type ReactNode } from "react";
import { Navigate, useLocation, useSearchParams } from "react-router-dom";
import { ArrowLeftRight, Eye, FileText, Loader2, Lock, NotebookPen, ShieldCheck, Upload } from "lucide-react";
import { Logo } from "@/components/quickscan/Logo";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";

// Consent screen for Supabase Auth's OAuth 2.1 server. External AI clients
// (Claude, ChatGPT) that connect to the gv-crm-mcp edge function land here to
// ask for access to the signed-in user's CRM account.

export const OAUTH_RETURN_KEY = "oauth_return";
const CRM_ROLES = ["admin", "super_admin", "crm_user"];

// Greenvision brand, taken from the logo: forest green, charcoal, copper.
const GV_GREEN = "#01473A";
const GV_GREEN_DARK = "#013328";

type Details = {
  clientName: string;
  clientLogo: string | null;
  redirectHost: string;
  email: string;
  scopes: string[];
};

const PERMISSIONS: { icon: typeof Eye; title: string; text: string }[] = [
  { icon: Eye, title: "Projecten bekijken", text: "Projectgegevens en de laatste notities." },
  { icon: FileText, title: "Documenten lezen", text: "Bestanden in de SharePoint-projectmappen." },
  { icon: Upload, title: "Bestanden toevoegen", text: "Nieuwe bestanden en nieuwe versies in een projectmap." },
  { icon: NotebookPen, title: "Notities schrijven", text: "Een notitie aan een projectdossier toevoegen." },
];

export default function OAuthConsentPage() {
  const { user, userRole, loading, signOut } = useAuth();
  const location = useLocation();
  const [params] = useSearchParams();
  const authorizationId = params.get("authorization_id");
  const [details, setDetails] = useState<Details | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<"approve" | "deny" | null>(null);

  useEffect(() => {
    if (!user || !authorizationId) return;
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase.auth.oauth.getAuthorizationDetails(authorizationId);
      if (cancelled) return;
      if (error || !data) {
        setError(error?.message ?? "Dit verzoek is niet (meer) geldig. Start het koppelen opnieuw vanuit de app.");
        return;
      }
      // Already consented earlier: Supabase answers with a redirect straight away.
      if ("redirect_url" in data) {
        window.location.assign(data.redirect_url);
        return;
      }
      setDetails({
        clientName: data.client.name || "Een externe app",
        clientLogo: data.client.logo_uri || null,
        redirectHost: safeHost(data.redirect_uri),
        email: data.user.email,
        scopes: (data.scope ?? "").split(" ").filter(Boolean),
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [user, authorizationId]);

  if (!authorizationId) {
    return <Notice title="Ongeldige link" text="Er ontbreekt een verzoek-id. Start het koppelen opnieuw vanuit Claude of ChatGPT." />;
  }

  if (loading) return <Loading />;

  // Not signed in: remember this page, sign in, and RootRedirect brings the user back.
  if (!user) {
    sessionStorage.setItem(OAUTH_RETURN_KEY, `${location.pathname}${location.search}`);
    return <Navigate to="/auth" replace />;
  }

  if (!userRole || !CRM_ROLES.includes(userRole)) {
    return <Notice title="Geen toegang" text="Alleen CRM-gebruikers kunnen een AI-app aan het Greenvision CRM koppelen. Vraag Stan om toegang." />;
  }

  const decide = async (approve: boolean) => {
    setBusy(approve ? "approve" : "deny");
    const { data, error } = approve
      ? await supabase.auth.oauth.approveAuthorization(authorizationId, { skipBrowserRedirect: true })
      : await supabase.auth.oauth.denyAuthorization(authorizationId, { skipBrowserRedirect: true });
    if (error || !data) {
      setBusy(null);
      setError(error?.message ?? "Er ging iets mis bij het verwerken van je keuze.");
      return;
    }
    window.location.assign(data.redirect_url);
  };

  if (error) return <Notice title="Koppelen mislukt" text={error} />;
  if (!details) return <Loading />;

  const initial = details.email.charAt(0).toUpperCase();

  return (
    <Frame>
      <div className="px-8 pt-8 pb-6 animate-in fade-in slide-in-from-bottom-2 duration-500">
        {/* Who connects to whom */}
        <div className="flex items-center justify-center gap-3">
          <AppTile>
            {details.clientLogo ? (
              <img src={details.clientLogo} alt="" className="h-9 w-9 rounded-lg object-contain" />
            ) : (
              <span className="text-2xl font-semibold text-neutral-800">{details.clientName.charAt(0)}</span>
            )}
          </AppTile>
          <div className="flex items-center gap-1 text-neutral-400">
            <span className="h-px w-5 border-t border-dashed border-neutral-300" />
            <span className="grid h-7 w-7 place-items-center rounded-full border border-neutral-200 bg-white">
              <ArrowLeftRight className="h-3.5 w-3.5" />
            </span>
            <span className="h-px w-5 border-t border-dashed border-neutral-300" />
          </div>
          <AppTile>
            <img src="/pwa-192.png" alt="Greenvision" className="h-full w-full rounded-[14px] object-cover" />
          </AppTile>
        </div>

        <h1 className="mt-6 text-center text-[22px] font-semibold leading-tight tracking-tight text-neutral-900">
          {details.clientName} wil verbinden met het Greenvision&nbsp;CRM
        </h1>

        {/* Account */}
        <div className="mx-auto mt-4 flex w-fit items-center gap-2.5 rounded-full border border-neutral-200 bg-neutral-50 py-1 pl-1 pr-3">
          <span className="grid h-6 w-6 place-items-center rounded-full text-[11px] font-semibold text-white" style={{ background: GV_GREEN }}>
            {initial}
          </span>
          <span className="text-sm text-neutral-700">{details.email}</span>
          <button onClick={() => signOut?.()} className="text-xs text-neutral-400 underline-offset-2 hover:text-neutral-700 hover:underline">
            niet jij?
          </button>
        </div>
      </div>

      <div className="border-t border-neutral-100 px-8 py-6">
        <p className="text-xs font-medium uppercase tracking-[0.14em] text-neutral-400">Namens jou mag {details.clientName}</p>
        <ul className="mt-4 space-y-3.5">
          {PERMISSIONS.map(({ icon: Icon, title, text }, i) => (
            <li
              key={title}
              className="flex gap-3 animate-in fade-in slide-in-from-left-1"
              style={{ animationDelay: `${120 + i * 70}ms`, animationDuration: "400ms", animationFillMode: "both" }}
            >
              <span className="mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-lg" style={{ background: "#01473A0F", color: GV_GREEN }}>
                <Icon className="h-4 w-4" />
              </span>
              <span>
                <span className="block text-sm font-medium text-neutral-900">{title}</span>
                <span className="block text-sm text-neutral-500">{text}</span>
              </span>
            </li>
          ))}
        </ul>

        <div className="mt-5 flex gap-3 rounded-xl border px-4 py-3" style={{ borderColor: "#C98B5B40", background: "#C98B5B0D" }}>
          <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" style={{ color: "#A8693A" }} />
          <p className="text-[13px] leading-relaxed text-neutral-700">
            Verwijderen of overschrijven kan nooit. Elke schrijfactie komt in het activiteitenlog, en je hebt dezelfde rechten als in de app.
          </p>
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 border-t border-neutral-100 bg-neutral-50/60 px-8 py-5">
        <p className="flex items-center gap-1.5 text-xs text-neutral-400">
          <Lock className="h-3 w-3" /> Terug naar {details.redirectHost}
        </p>
        <div className="flex gap-2">
          <button
            disabled={busy !== null}
            onClick={() => decide(false)}
            className="h-10 rounded-lg border border-neutral-200 bg-white px-4 text-sm font-medium text-neutral-700 transition hover:bg-neutral-50 disabled:opacity-50"
          >
            {busy === "deny" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Weigeren"}
          </button>
          <button
            disabled={busy !== null}
            onClick={() => decide(true)}
            className="h-10 min-w-[108px] rounded-lg px-5 text-sm font-medium text-white shadow-sm transition disabled:opacity-60"
            style={{ background: busy === "approve" ? GV_GREEN_DARK : GV_GREEN }}
            onMouseEnter={(e) => (e.currentTarget.style.background = GV_GREEN_DARK)}
            onMouseLeave={(e) => (e.currentTarget.style.background = GV_GREEN)}
          >
            {busy === "approve" ? <Loader2 className="mx-auto h-4 w-4 animate-spin" /> : "Toestaan"}
          </button>
        </div>
      </div>

      {details.scopes.length > 0 && (
        <details className="group border-t border-neutral-100 px-8 py-3 text-xs text-neutral-400">
          <summary className="cursor-pointer select-none list-none hover:text-neutral-600">Technische details</summary>
          <p className="mt-2 font-mono">scopes: {details.scopes.join(" ")}</p>
        </details>
      )}
    </Frame>
  );
}

function Frame({ children }: { children: ReactNode }) {
  return (
    <div
      className="relative flex min-h-screen flex-col items-center justify-center px-4 py-10"
      style={{
        background:
          "radial-gradient(900px 500px at 15% -10%, #01473A14, transparent 60%), radial-gradient(700px 420px at 100% 110%, #C98B5B1F, transparent 60%), #F6F3EE",
      }}
    >
      <Logo className="mb-8 h-9 object-contain" />
      <div className="w-full max-w-[460px] overflow-hidden rounded-2xl border border-black/[0.06] bg-white shadow-[0_1px_2px_rgba(0,0,0,0.04),0_12px_40px_-12px_rgba(1,71,58,0.18)]">
        <div className="h-[3px]" style={{ background: "linear-gradient(90deg, #01473A, #3E7A5E 45%, #C98B5B)" }} />
        {children}
      </div>
      <p className="mt-6 text-xs text-neutral-400">Greenvision Energy · koppeling intrekken kan altijd in de instellingen van de app</p>
    </div>
  );
}

function AppTile({ children }: { children: ReactNode }) {
  return (
    <span className="grid h-14 w-14 place-items-center rounded-2xl border border-neutral-200 bg-white shadow-sm">{children}</span>
  );
}

function Loading() {
  return (
    <Frame>
      <div className="flex items-center justify-center gap-2 px-8 py-16 text-sm text-neutral-500">
        <Loader2 className="h-4 w-4 animate-spin" style={{ color: GV_GREEN }} /> Verzoek ophalen…
      </div>
    </Frame>
  );
}

function Notice({ title, text }: { title: string; text: string }) {
  return (
    <Frame>
      <div className="px-8 py-10 text-center">
        <h1 className="text-lg font-semibold text-neutral-900">{title}</h1>
        <p className="mx-auto mt-2 max-w-sm text-sm leading-relaxed text-neutral-500">{text}</p>
      </div>
    </Frame>
  );
}

function safeHost(uri: string): string {
  try {
    return new URL(uri).host;
  } catch {
    return uri;
  }
}
